/**
 * Utilitaires pour la gestion des abonnements
 * Fonctions partagées entre frontend et Edge Functions
 */

/**
 * Normalise un nom de plan d'abonnement
 * Retourne toujours: 'Boost', 'Scale', ou 'Lunch' (capitalisés)
 * 
 * Cette fonction doit correspondre exactement à la fonction SQL normalize_plan_name()
 * pour garantir la cohérence entre frontend et backend.
 * 
 * @param plan - Nom du plan (peut être en minuscules, majuscules, ou mixte)
 * @returns Nom du plan normalisé: 'Boost', 'Scale', ou 'Lunch'
 */
export function normalizePlanName(plan: string | null | undefined): 'Boost' | 'Scale' | 'Lunch' {
  // Retourner 'Lunch' comme fallback si l'entrée est null, undefined, ou vide
  if (!plan || typeof plan !== 'string' || plan.trim() === '') {
    return 'Lunch';
  }
  
  // Convertir en minuscules pour comparaison
  const planLower = plan.trim().toLowerCase();
  
  // Vérifier Scale en premier (plus spécifique)
  if (planLower.includes('scale')) {
    return 'Scale';
  }
  
  // Vérifier Boost
  if (planLower.includes('boost')) {
    return 'Boost';
  }
  
  // Vérifier Launch/Lunch
  if (planLower.includes('launch') || planLower.includes('lunch')) {
    return 'Lunch';
  }
  
  // Fallback par défaut
  return 'Lunch';
}

/**
 * Vérifie si un plan est valide (Boost, Scale, ou Lunch)
 */
export function isValidPlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  const normalized = normalizePlanName(plan);
  return normalized === 'Boost' || normalized === 'Scale' || normalized === 'Lunch';
}

/**
 * Compare deux plans et retourne si le premier est supérieur ou égal au second
 * Hiérarchie: Lunch (0) < Boost (1) < Scale (2)
 */
export function isPlanGreaterOrEqual(
  userPlan: string | null | undefined,
  requiredPlan: 'Boost' | 'Scale' | 'Lunch'
): boolean {
  const planHierarchy: Record<string, number> = {
    'Lunch': 0,
    'Boost': 1,
    'Scale': 2,
  };
  
  const normalizedUserPlan = normalizePlanName(userPlan);
  const userPlanLevel = planHierarchy[normalizedUserPlan] ?? 0;
  const requiredPlanLevel = planHierarchy[requiredPlan] ?? 0;
  
  return userPlanLevel >= requiredPlanLevel;
}

/**
 * Obtient le niveau hiérarchique d'un plan
 */
export function getPlanLevel(plan: string | null | undefined): number {
  const planHierarchy: Record<string, number> = {
    'Lunch': 0,
    'Boost': 1,
    'Scale': 2,
  };
  
  const normalizedPlan = normalizePlanName(plan);
  return planHierarchy[normalizedPlan] ?? 0;
}

